import React, { useState, useEffect } from 'react';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import priceService from '../../../services/priceService';

const SwapWidget = ({ assets, exchangeRate, lastUpdate }) => {
  const [fromAsset, setFromAsset] = useState('eth');
  const [toAsset, setToAsset] = useState('usdc');
  const [fromAmount, setFromAmount] = useState('1.0');
  const [toAmount, setToAmount] = useState('0.00');
  const [slippage, setSlippage] = useState('0.5');
  const [liveRate, setLiveRate] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch live exchange rate
  useEffect(() => {
    const fetchRate = async () => {
      if (fromAsset && toAsset) {
        setLoading(true);
        const rate = await priceService?.getExchangeRate(
          fromAsset?.toUpperCase(),
          toAsset?.toUpperCase()
        );
        setLiveRate(rate);
        setLoading(false);
        
        // Recalculate toAmount with new rate
        if (rate && fromAmount && !isNaN(fromAmount)) {
          setToAmount((parseFloat(fromAmount) * rate)?.toFixed(6));
        }
      }
    };

    fetchRate();
    // Refresh rate every 30 seconds
    const interval = setInterval(fetchRate, 30000);
    return () => clearInterval(interval);
  }, [fromAsset, toAsset]);

  const assetOptions = assets?.map(asset => ({
    value: asset?.id,
    label: `${asset?.name} (${asset?.symbol})`,
    description: `Balance: ${asset?.balance}`
  }));

  const handleSwapDirection = () => {
    const tempAsset = fromAsset;
    setFromAsset(toAsset);
    setToAsset(tempAsset);
    const tempAmount = fromAmount;
    setFromAmount(toAmount);
    setToAmount(tempAmount);
  };

  const handleFromAmountChange = (e) => {
    const value = e?.target?.value;
    setFromAmount(value);
    if (value && !isNaN(value) && liveRate) {
      setToAmount((parseFloat(value) * liveRate)?.toFixed(6));
    }
  };

  const handleSwap = () => {
    console.log('Swap initiated:', { fromAsset, toAsset, fromAmount, toAmount, slippage });
  };

  const estimatedFee = liveRate && fromAmount ? (parseFloat(fromAmount) * liveRate * 0.003)?.toFixed(4) : '0.00';
  const estimatedTime = '~30 seconds';

  return (
    <div className="card">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-foreground mb-1">Swap Assets</h3>
        <p className="text-sm text-muted-foreground">Exchange between supported assets</p>
      </div>

      {/* From Section */}
      <div className="space-y-4">
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <label className="text-xs text-muted-foreground mb-2 block">From</label>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Input
                type="text"
                value={fromAmount}
                onChange={handleFromAmountChange}
                placeholder="0.0"
                className="text-2xl font-bold border-0 bg-transparent p-0 h-auto focus-visible:ring-0"
              />
            </div>
            <div className="w-48">
              <Select
                options={assetOptions}
                value={fromAsset}
                onChange={setFromAsset}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Balance: {assets?.find(a => a?.id === fromAsset)?.balance} {assets?.find(a => a?.id === fromAsset)?.symbol}
          </p>
        </div>

        {/* Swap Direction Button */}
        <div className="flex justify-center">
          <button
            onClick={handleSwapDirection}
            className="w-10 h-10 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-smooth flex items-center justify-center"
          >
            <Icon name="ArrowDownUp" size={20} />
          </button>
        </div>

        {/* To Section */}
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <label className="text-xs text-muted-foreground mb-2 block">To</label>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Input
                type="text"
                value={toAmount}
                readOnly
                placeholder="0.0"
                className="text-2xl font-bold border-0 bg-transparent p-0 h-auto focus-visible:ring-0"
              />
            </div>
            <div className="w-48">
              <Select
                options={assetOptions}
                value={toAsset}
                onChange={setToAsset}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Balance: {assets?.find(a => a?.id === toAsset)?.balance} {assets?.find(a => a?.id === toAsset)?.symbol}
          </p>
        </div>
      </div>

      {/* Transaction Details */}
      <div className="mt-6 p-4 rounded-lg bg-muted/20 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Rate</span>
          <div className="flex items-center gap-2">
            {loading ? (
              <span className="text-xs text-muted-foreground">Loading...</span>
            ) : (
              <span className="font-medium text-foreground">
                1 {fromAsset?.toUpperCase()} = {liveRate?.toFixed(6) || '---'} {toAsset?.toUpperCase()}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Estimated Fee</span>
          <span className="font-medium text-foreground">${estimatedFee}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Slippage Tolerance</span>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={slippage}
              onChange={(e) => setSlippage(e?.target?.value)}
              className="w-16 px-2 py-1 text-right rounded border border-border bg-background text-foreground text-sm"
            />
            <span className="text-foreground">%</span>
          </div>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Estimated Time</span>
          <span className="font-medium text-foreground">{estimatedTime}</span>
        </div>
      </div>

      {/* Swap Button */}
      <Button
        variant="default"
        size="lg"
        onClick={handleSwap}
        fullWidth
        className="mt-6"
        iconName="RefreshCw"
        iconPosition="left"
      >
        Swap Assets
      </Button>
    </div>
  );
};

export default SwapWidget;